import type { ClosedGapDecision } from "./deskSession";
import {
  commitIdleMint,
  DEFAULT_IDLE_INBOX,
  dropIdleFerries,
  EMPTY_IDLE_INBOX_CLOCK,
  hydrateIdleInboxClock,
  hydrateIdleInboxSettings,
  idleCallsRoom,
  idleWindowRoom,
  IDLE_INBOX_AFTER_MIN,
  type IdleInboxClock,
  type IdleInboxSettings,
} from "./idleInbox";
import {
  fetchCharterInquiries,
  generateSeasonalInquiries,
  seedFromDate,
  type FetchInquiriesInput,
} from "./inbox";
import { isCharterAssignable } from "./maintenance";
import type { CharterRequest, InquirySource } from "./types";
import { isCharterInboxItem } from "./voiceDesk";

/** Incoming auto-generated charter. Distinct from manual Fetch (`demo` / `grok` / `automatic`). */
export const AUTO_INBOX_SOURCE: InquirySource = "trickle";

/** 24h sim / wall day used to convert RFQs-per-day ↔ interval. */
export const AUTO_INBOX_SIM_DAY_MIN = 24 * 60;

/** Old persist default — remap to the quiet small-charter rate when hydrating. */
export const AUTO_INBOX_LEGACY_INTERVAL_MIN = 12;

export const AUTO_INBOX_RFQS_PER_DAY = { min: 1, max: 96, default: 3 } as const;
export const AUTO_INBOX_INTERVAL_MIN = { min: 15, max: 1440, default: 480 } as const;
export const AUTO_INBOX_MAX_PENDING = { min: 1, max: 12, default: 6 } as const;
export const AUTO_INBOX_MAX_PER_TICK = { min: 1, max: 2, default: 1 } as const;

/** How often the open desk re-checks the throttle (wall clock). */
export const AUTO_INBOX_POLL_MS = 30_000;

/** Stepped daily-rate slider. 3 ≈ small CYPD charter; 96 ≈ every 15 min (busy desk). */
export const AUTO_INBOX_RATE_CHOICES = [1, 2, 3, 4, 6, 8, 12, 24, 48, 96] as const;
export const AUTO_INBOX_PENDING_CHOICES = [3, 4, 6, 8, 10, 12] as const;

export type AutoInboxSettings = {
  /** When true, the open desk drips Inbox RFQs (cheap Grok if keyed, else Classic). */
  enabled: boolean;
  /** RFQs per 24h sim/wall day while the desk is open. Source of truth for the Settings slider. */
  rfqsPerSimDay: number;
  /** Minutes between ticks (derived from daily rate; wall clock, or sim minutes when passed). */
  intervalMin: number;
  /** Cap on pending (status=new) charter rows. */
  maxPending: number;
  /** Requests created on an allowed tick. */
  maxPerTick: number;
  /** Cost-capped idle / away Inbox refill. Schedule catch-up stays off. */
  idle: IdleInboxSettings;
};

export type AutoInboxClock = {
  lastTickAtUtc: string | null;
  /**
   * Set on away reopen. Allows one Inbox RFQ (cap-respecting) without
   * wall-clock catch-up of the closed gap. Schedule brain stays frozen.
   */
  reopenMintDue?: boolean;
  /** Idle-window RFQ count and UTC-day Grok call counter. */
  idle: IdleInboxClock;
};

export const DEFAULT_AUTO_INBOX: AutoInboxSettings = {
  enabled: true,
  rfqsPerSimDay: AUTO_INBOX_RFQS_PER_DAY.default,
  intervalMin: AUTO_INBOX_INTERVAL_MIN.default,
  maxPending: AUTO_INBOX_MAX_PENDING.default,
  maxPerTick: AUTO_INBOX_MAX_PER_TICK.default,
  idle: DEFAULT_IDLE_INBOX,
};

export const EMPTY_AUTO_INBOX_CLOCK: AutoInboxClock = {
  lastTickAtUtc: null,
  reopenMintDue: false,
  idle: EMPTY_IDLE_INBOX_CLOCK,
};

export type AutoInboxSkipReason =
  | "paused"
  | "interval"
  | "cap"
  | "no_scenery"
  | "no_fleet"
  | "away"
  | "cooldown"
  | "window"
  | "budget"
  | "not_idle";

export type AutoInboxDecision =
  | { ok: true; count: number }
  | { ok: false; reason: AutoInboxSkipReason };

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function intervalMinFromDailyRate(rfqsPerSimDay: number): number {
  const n = clampInt(rfqsPerSimDay, AUTO_INBOX_RFQS_PER_DAY.min, AUTO_INBOX_RFQS_PER_DAY.max, AUTO_INBOX_RFQS_PER_DAY.default);
  return clampInt(
    Math.round(AUTO_INBOX_SIM_DAY_MIN / n),
    AUTO_INBOX_INTERVAL_MIN.min,
    AUTO_INBOX_INTERVAL_MIN.max,
    AUTO_INBOX_INTERVAL_MIN.default,
  );
}

export function dailyRateFromIntervalMin(intervalMin: number): number {
  const interval = clampInt(
    intervalMin,
    AUTO_INBOX_INTERVAL_MIN.min,
    AUTO_INBOX_INTERVAL_MIN.max,
    AUTO_INBOX_INTERVAL_MIN.default,
  );
  return clampInt(
    Math.round(AUTO_INBOX_SIM_DAY_MIN / interval),
    AUTO_INBOX_RFQS_PER_DAY.min,
    AUTO_INBOX_RFQS_PER_DAY.max,
    AUTO_INBOX_RFQS_PER_DAY.default,
  );
}

export function nearestAutoInboxRate(rfqsPerSimDay: number): (typeof AUTO_INBOX_RATE_CHOICES)[number] {
  const n = clampInt(rfqsPerSimDay, AUTO_INBOX_RFQS_PER_DAY.min, AUTO_INBOX_RFQS_PER_DAY.max, AUTO_INBOX_RFQS_PER_DAY.default);
  let best: (typeof AUTO_INBOX_RATE_CHOICES)[number] = AUTO_INBOX_RATE_CHOICES[0];
  let bestDist = Number.POSITIVE_INFINITY;
  for (const choice of AUTO_INBOX_RATE_CHOICES) {
    const dist = Math.abs(choice - n);
    if (dist < bestDist) {
      best = choice;
      bestDist = dist;
    }
  }
  return best;
}

export function autoInboxRateCaption(rfqsPerSimDay: number): string {
  const n = nearestAutoInboxRate(rfqsPerSimDay);
  const interval = intervalMinFromDailyRate(n);
  if (n <= 3) return `Small charter · ~${n} RFQ${n === 1 ? "" : "s"} / sim day (every ${Math.round(interval / 60)} h)`;
  if (n <= 12) return `Steady · ~${n} / sim day`;
  if (n <= 48) return `Busy desk · ~${n} / sim day (every ${interval} min)`;
  return `Hot desk · ~${n} / sim day (every ${interval} min)`;
}

/** Derived interval list for older Settings selects — prefer AUTO_INBOX_RATE_CHOICES. */
export const AUTO_INBOX_INTERVAL_CHOICES = AUTO_INBOX_RATE_CHOICES.map((n) => intervalMinFromDailyRate(n));

export function hydrateAutoInboxSettings(raw?: Partial<AutoInboxSettings> | null): AutoInboxSettings {
  let rfqs: number;
  if (raw?.rfqsPerSimDay != null && Number.isFinite(Number(raw.rfqsPerSimDay))) {
    rfqs = clampInt(
      raw.rfqsPerSimDay,
      AUTO_INBOX_RFQS_PER_DAY.min,
      AUTO_INBOX_RFQS_PER_DAY.max,
      AUTO_INBOX_RFQS_PER_DAY.default,
    );
  } else if (
    raw?.intervalMin != null &&
    Number.isFinite(Number(raw.intervalMin)) &&
    Number(raw.intervalMin) !== AUTO_INBOX_LEGACY_INTERVAL_MIN
  ) {
    rfqs = dailyRateFromIntervalMin(Number(raw.intervalMin));
  } else {
    rfqs = AUTO_INBOX_RFQS_PER_DAY.default;
  }
  return {
    enabled: raw?.enabled !== false,
    rfqsPerSimDay: rfqs,
    intervalMin: intervalMinFromDailyRate(rfqs),
    maxPending: clampInt(
      raw?.maxPending,
      AUTO_INBOX_MAX_PENDING.min,
      AUTO_INBOX_MAX_PENDING.max,
      AUTO_INBOX_MAX_PENDING.default,
    ),
    maxPerTick: clampInt(
      raw?.maxPerTick,
      AUTO_INBOX_MAX_PER_TICK.min,
      AUTO_INBOX_MAX_PER_TICK.max,
      AUTO_INBOX_MAX_PER_TICK.default,
    ),
    idle: hydrateIdleInboxSettings(raw?.idle),
  };
}

export function hydrateAutoInboxClock(raw?: Partial<AutoInboxClock> | null): AutoInboxClock {
  const last = raw?.lastTickAtUtc;
  const reopenMintDue = raw?.reopenMintDue === true;
  const idle = hydrateIdleInboxClock(raw?.idle);
  if (typeof last !== "string" || !last.trim()) return { lastTickAtUtc: null, reopenMintDue, idle };
  const t = new Date(last);
  if (Number.isNaN(t.getTime())) return { lastTickAtUtc: null, reopenMintDue, idle };
  return { lastTickAtUtc: t.toISOString(), reopenMintDue, idle };
}

export function pendingCharterCount(inbox: ReadonlyArray<Pick<CharterRequest, "status" | "kind">>): number {
  return inbox.filter((row) => isCharterInboxItem(row) && row.status === "new").length;
}

export function wallElapsedMin(lastTickAtUtc: string | null | undefined, now: Date): number {
  if (!lastTickAtUtc) return Number.POSITIVE_INFINITY;
  const last = new Date(lastTickAtUtc);
  if (Number.isNaN(last.getTime())) return Number.POSITIVE_INFINITY;
  return (now.getTime() - last.getTime()) / 60_000;
}

/**
 * Throttle + cap. Local only — never implies an xAI call.
 * Due when wall minutes (or optional sim minutes) meet the interval.
 */
export function decideAutoInboxTick(input: {
  settings: AutoInboxSettings;
  lastTickAtUtc?: string | null;
  pendingCount: number;
  sceneryCount: number;
  fleetCount: number;
  now?: Date;
  /** Override wall-clock minutes since last tick. */
  wallElapsedMin?: number;
  /** Optional sim-clock minutes since last tick (time accel). */
  simElapsedMin?: number | null;
  /** Away-time pause — never mint a closed-gap pile. Schedule catch-up stays off. */
  closedGap?: ClosedGapDecision | null;
  /**
   * One Inbox RFQ after desk reopen / empty on-open seed.
   * Ignores interval and the away freeze. Still honors cap / scenery / fleet.
   * Never expands to `gap / interval` catch-up.
   */
  reopenMint?: boolean;
  /**
   * Minutes since the last pointer / key on this desk. Omit to keep the legacy
   * interval trickle (unit tests). The desk hook always passes it.
   */
  userIdleMin?: number | null;
  /** When true, a spent daily call budget blocks the tick instead of Classic. */
  hasGrokKey?: boolean;
  idleClock?: IdleInboxClock | null;
}): AutoInboxDecision {
  const settings = hydrateAutoInboxSettings(input.settings);
  const idle = settings.idle;
  if (!settings.enabled) return { ok: false, reason: "paused" };
  const away = Boolean(input.closedGap?.isClosedGap);
  const reopen = input.reopenMint === true;
  if (away && !reopen && !idle.enabled) return { ok: false, reason: "away" };
  if (input.sceneryCount < 2) return { ok: false, reason: "no_scenery" };
  if (input.fleetCount < 1) return { ok: false, reason: "no_fleet" };
  const room = settings.maxPending - input.pendingCount;
  if (room <= 0) return { ok: false, reason: "cap" };

  const now = input.now ?? new Date();
  const wall = input.wallElapsedMin ?? wallElapsedMin(input.lastTickAtUtc, now);
  const sim = input.simElapsedMin;
  const legacyDue =
    reopen ||
    wall >= settings.intervalMin ||
    (sim != null && Number.isFinite(sim) && sim >= settings.intervalMin);

  const trackIdle = idle.enabled && (input.userIdleMin != null || away || reopen);
  if (!trackIdle) {
    if (!legacyDue) return { ok: false, reason: "interval" };
    return { ok: true, count: Math.min(settings.maxPerTick, Math.max(1, room)) };
  }

  const clock = hydrateIdleInboxClock(input.idleClock);
  const window = idleWindowRoom(clock, now, idle);
  const callsRoom = idleCallsRoom(clock, now, idle);
  const userIdle = input.userIdleMin == null || input.userIdleMin >= IDLE_INBOX_AFTER_MIN;
  const allowed = reopen || away || userIdle;
  if (!allowed) return { ok: false, reason: "not_idle" };
  if (window.room <= 0) return { ok: false, reason: "window" };
  const skipCooldown = reopen;
  const cooldownOk = input.lastTickAtUtc == null || wall >= idle.cooldownMin;
  if (!skipCooldown && !cooldownOk) return { ok: false, reason: "cooldown" };
  if (input.hasGrokKey && callsRoom <= 0) return { ok: false, reason: "budget" };
  return { ok: true, count: Math.min(settings.maxPerTick, Math.max(1, room), window.room) };
}

export type AutoInboxTickInput = FetchInquiriesInput & {
  settings: AutoInboxSettings;
  lastTickAtUtc?: string | null;
  pendingCount: number;
  simElapsedMin?: number | null;
  closedGap?: ClosedGapDecision | null;
  reopenMint?: boolean;
  userIdleMin?: number | null;
  hasGrokKey?: boolean;
  idleClock?: IdleInboxClock | null;
};

export type AutoInboxTickResult = {
  decision: AutoInboxDecision;
  requests: CharterRequest[];
  clock: AutoInboxClock;
  fallbackReason?: string;
  usedGrok?: boolean;
};

function decideTickFromInput(input: AutoInboxTickInput, now: Date): AutoInboxDecision {
  const settings = hydrateAutoInboxSettings(input.settings);
  const fleetCount = (input.fleet ?? []).filter((a) => isCharterAssignable(a)).length;
  return decideAutoInboxTick({
    settings,
    lastTickAtUtc: input.lastTickAtUtc ?? null,
    pendingCount: input.pendingCount,
    sceneryCount: input.scenery?.length ?? 0,
    fleetCount,
    now,
    simElapsedMin: input.simElapsedMin,
    closedGap: input.closedGap,
    reopenMint: input.reopenMint,
    userIdleMin: input.userIdleMin,
    hasGrokKey: input.hasGrokKey,
    idleClock: input.idleClock,
  });
}

function idleClockOf(input: AutoInboxTickInput): IdleInboxClock {
  return hydrateIdleInboxClock(input.idleClock);
}

function skippedClock(input: AutoInboxTickInput, decision: AutoInboxDecision, now: Date): AutoInboxClock {
  const lastTickAtUtc = input.lastTickAtUtc ?? null;
  const idle = idleClockOf(input);
  if (decision.ok === false && decision.reason === "away" && input.closedGap?.resetClocks) {
    return { lastTickAtUtc: now.toISOString(), reopenMintDue: false, idle };
  }
  if (decision.ok === false && (decision.reason === "window" || decision.reason === "budget")) {
    return { lastTickAtUtc, reopenMintDue: false, idle };
  }
  return {
    lastTickAtUtc,
    reopenMintDue: input.reopenMint === true,
    idle,
  };
}

function mintedClock(
  input: AutoInboxTickInput,
  now: Date,
  added: number,
  calls: number,
): AutoInboxClock {
  const settings = hydrateAutoInboxSettings(input.settings);
  return {
    lastTickAtUtc: now.toISOString(),
    reopenMintDue: false,
    idle: commitIdleMint(idleClockOf(input), now, settings.idle, added, calls),
  };
}

/** Idle mint may attach a reverse backhaul. maxPerTick still caps the rows that land. */
function capTrickleBatch(rows: CharterRequest[], cap: number): CharterRequest[] {
  if (cap <= 0) return [];
  const kept = rows.slice(0, cap);
  const keptIds = new Set(kept.map((row) => row.id));
  return kept.map((row) =>
    row.linkedRequestId && !keptIds.has(row.linkedRequestId)
      ? { ...row, linkedRequestId: undefined }
      : row,
  );
}

function classicTrickleRequests(input: AutoInboxTickInput, count: number, now: Date): CharterRequest[] {
  const settings = hydrateAutoInboxSettings(input.settings);
  const salt = input.pendingCount + Math.floor(now.getTime() / 60_000);
  const generated = generateSeasonalInquiries({
    ...input,
    apiKey: undefined,
    forceDemo: false,
    count,
    now,
    seed: input.seed ?? seedFromDate(now, salt),
  });
  const room = Math.max(0, settings.maxPending - input.pendingCount);
  return dropIdleFerries(capTrickleBatch(generated, Math.min(count, room))).map((row) => ({
    ...row,
    source: AUTO_INBOX_SOURCE,
  }));
}

/**
 * One idle tick: seasonal local RFQs (company home / Celtic origins). Never calls Grok.
 * Advances the clock only when a tick was due (even if generation returned []).
 */
export function runAutoInboxTick(input: AutoInboxTickInput): AutoInboxTickResult {
  const now = input.now ?? new Date();
  const decision = decideTickFromInput(input, now);
  if (!decision.ok) {
    return { decision, requests: [], clock: skippedClock(input, decision, now) };
  }

  const requests = classicTrickleRequests(input, decision.count, now);
  return {
    decision,
    requests,
    clock: mintedClock(input, now, requests.length, 0),
  };
}

/**
 * Desk-open idle / reopen mint. Prefers cheap grok-3-mini when a key is present
 * (same reliability rules as Fetch, max 2 Grok calls per tick). Classic only
 * after no key, hard API fail, or last-resort empty / filter-emptied set.
 * Schedule board is not touched.
 */
export async function runAutoInboxTickAsync(input: AutoInboxTickInput): Promise<AutoInboxTickResult> {
  const now = input.now ?? new Date();
  const keyed: AutoInboxTickInput = {
    ...input,
    hasGrokKey: input.hasGrokKey ?? Boolean(input.apiKey?.trim()),
  };
  const decision = decideTickFromInput(keyed, now);
  if (!decision.ok) {
    return { decision, requests: [], clock: skippedClock(keyed, decision, now) };
  }

  const key = input.apiKey?.trim();
  if (!key) {
    const requests = classicTrickleRequests(keyed, decision.count, now);
    return {
      decision,
      requests,
      clock: mintedClock(keyed, now, requests.length, 0),
      usedGrok: false,
    };
  }

  const settings = hydrateAutoInboxSettings(keyed.settings);
  const callsRoom = idleCallsRoom(idleClockOf(keyed), now, settings.idle);
  let calls = 0;
  const baseFetch = input.fetchImpl ?? fetch;
  const countingFetch: typeof fetch = async (url, init) => {
    calls += 1;
    return baseFetch(url, init);
  };
  const fetched = await fetchCharterInquiries({
    ...keyed,
    apiKey: key,
    forceDemo: false,
    count: decision.count,
    now,
    inboxPromptKind: "idle",
    inboxCallBudget: callsRoom,
    fetchImpl: countingFetch,
    seed: keyed.seed ?? seedFromDate(now, keyed.pendingCount + 19),
  });
  const room = Math.max(0, settings.maxPending - keyed.pendingCount);
  if (fetched.ok && fetched.mode === "grok" && fetched.requests.length) {
    const requests = dropIdleFerries(fetched.requests)
      .slice(0, room)
      .map((row) => ({ ...row, source: "grok" as InquirySource }));
    if (requests.length) {
      return {
        decision,
        requests,
        clock: mintedClock(keyed, now, requests.length, calls),
        usedGrok: true,
      };
    }
  }

  const droppedFerry = fetched.ok && fetched.mode === "grok" && fetched.requests.length > 0;
  const fallbackReason =
    fetched.ok && fetched.fallbackReason
      ? fetched.fallbackReason
      : !fetched.ok
        ? `${fetched.error} Classic templates used.`
        : droppedFerry
          ? "Idle mint dropped an empty ferry. Classic templates used."
          : undefined;
  const classic = dropIdleFerries(
    fetched.ok && fetched.mode !== "grok" && fetched.requests.length
      ? fetched.requests.map((row) => ({ ...row, source: AUTO_INBOX_SOURCE }))
      : classicTrickleRequests(keyed, decision.count, now),
  ).slice(0, room);
  return {
    decision,
    requests: classic,
    clock: mintedClock(keyed, now, classic.length, calls),
    usedGrok: false,
    fallbackReason,
  };
}

export function autoInboxStatusLabel(
  settings: AutoInboxSettings,
  pendingCount: number,
): { live: boolean; text: string } {
  const s = hydrateAutoInboxSettings(settings);
  if (!s.enabled) return { live: false, text: "Batch off" };
  return {
    live: true,
    text: `One batch / session · ${pendingCount} new`,
  };
}
